import { useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';

import { useDb } from '../firebase/FirebaseContext';
import {
  applySovrnOverlay,
  SOVRN_OVERLAY_COLLECTION,
  SOVRN_OVERLAY_DOC,
} from '../lib/sovrnClickout';

/**
 * Subscribes to the Sovrn click-out overlay doc
 * (`theaWebConfig/sovrnClickout`, world-readable, server-write-only) so the
 * daily canary's automatic unwrap/re-wrap decisions reach users — including
 * already-open tabs — without a deploy. See sovrnClickout.ts for the safety
 * shape: removals always win over restores, and restores are bounded to
 * merchants already listed as broken in the code, so every failure mode here
 * (doc missing, malformed, listener error) falls back to at-worst plain
 * merchant URLs, never a broken wrapped click.
 *
 * Mounted once from App (SovrnOverlayMount), inside FirebaseProvider.
 */
export function useSovrnOverlay(): void {
  const db = useDb();
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, SOVRN_OVERLAY_COLLECTION, SOVRN_OVERLAY_DOC),
      (snap) => {
        // Read both fields RAW — no `?? []` default. An absent removedHosts
        // must fail the isArray guard below and leave the previous overlay
        // standing, because coercing it to [] would CLEAR the removals and
        // re-wrap a merchant the canary had deliberately switched off. A
        // missing doc is different: that legitimately means "no overlay".
        const removed: unknown = snap.exists() ? snap.data()?.removedHosts : [];
        const restored: unknown = snap.exists() ? snap.data()?.restoredHosts : [];
        const strings = (v: unknown) =>
          Array.isArray(v) ? v.filter((h): h is string => typeof h === 'string') : [];
        if (Array.isArray(removed)) {
          applySovrnOverlay(strings(removed), strings(restored));
        }
      },
      () => {
        // Fail open: keep whatever overlay we have (initially empty = the
        // baked-in allowlist behavior). Telemetry-free by design — the canary
        // itself is the monitor for this system.
      },
    );
    return unsub;
  }, [db]);
}
